import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, X, Loader2, MapPin, Clock, Flame } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";

const errorHeadline = (s) => {
  if (s === "expired") return "SESSION EXPIRED";
  if (s === "timeout") return "STILL PROCESSING";
  return "SOMETHING WENT WRONG";
};

const timeoutOrError = (s) => {
  if (s === "timeout") return "Your payment is still being checked. Refresh in a moment or check your email.";
  return "If you were charged, contact us at hello@blazingedge.bbq.";
};

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const clearRef = useRef(clear);
  clearRef.current = clear;
  const pollCount = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setOrder(data.order);
          clearRef.current();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        pollCount.current += 1;
        if (pollCount.current >= 10) {
          setStatus("timeout");
          return;
        }
        timerRef.current = setTimeout(poll, 2000);
      } catch (e) {
        setStatus("error");
      }
    };
    poll();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [sessionId]);

  return (
    <div data-testid="checkout-success-page" className="max-w-3xl mx-auto px-6 py-20 min-h-[70vh] flex flex-col items-center justify-center text-center">
      {status === "checking" && (
        <>
          <Loader2 className="text-brand animate-spin" size={48} />
          <h1 className="font-display text-5xl text-cream mt-6">CONFIRMING YOUR ORDER...</h1>
          <p className="text-[#A19D94] mt-3">Hang tight while we light the smoker.</p>
        </>
      )}

      {status === "paid" && (
        <>
          <div className="w-20 h-20 border-2 border-brand flex items-center justify-center teal-glow">
            <Check className="text-brand" size={40} strokeWidth={3} />
          </div>
          <div className="text-brand font-display text-sm tracking-[0.4em] mt-6">ORDER CONFIRMED</div>
          <h1 className="font-display text-5xl sm:text-7xl text-cream leading-none mt-3">YOU'RE IN.</h1>
          <p className="text-[#C8C4BB] mt-6 max-w-md">
            We've fired up your order. Show this confirmation at pickup.
          </p>

          {order && (
            <div className="mt-10 w-full bg-ink-800 border border-ink-600 p-6 text-left">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <div className="text-xs tracking-[0.3em] text-[#A19D94]">PICKUP</div>
                  <div className="flex items-start gap-2 mt-2">
                    <MapPin size={16} className="text-brand mt-1" />
                    <div>
                      <div className="font-display text-xl text-cream">GOSFORD SHOWGROUNDS</div>
                      <div className="text-sm text-[#A19D94]">Showground Road, Gosford NSW</div>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-xs tracking-[0.3em] text-[#A19D94]">WHEN</div>
                  <div className="flex items-start gap-2 mt-2">
                    <Clock size={16} className="text-brand mt-1" />
                    <div>
                      <div className="font-display text-xl text-cream">{order.pickup_date} @ {order.pickup_time}</div>
                      <div className="text-sm text-[#A19D94]">Be on time. Brisket waits for no one.</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-ink-600 mt-6 pt-6">
                <div className="text-xs tracking-[0.3em] text-[#A19D94] mb-3">ORDER</div>
                <ul className="space-y-2">
                  {order.items?.map((it) => (
                    <li key={it.product_id} className="flex justify-between text-cream">
                      <span>{it.quantity}× {it.name}</span>
                      <span className="font-display">${it.line_total.toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex justify-between items-end mt-4 pt-4 border-t border-ink-600">
                  <span className="font-display text-xl text-cream tracking-widest">TOTAL</span>
                  <span className="font-display text-3xl text-brand">${order.total.toFixed(2)} AUD</span>
                </div>
              </div>
            </div>
          )}

          <Link to="/shop" className="mt-10 inline-block px-10 h-14 bg-brand text-ink-900 font-display text-2xl tracking-widest leading-[3.5rem]">
            BACK TO THE PIT
          </Link>
        </>
      )}

      {(status === "expired" || status === "timeout" || status === "error") && (
        <>
          <div className="w-20 h-20 border-2 border-[#D9381E] flex items-center justify-center">
            <X className="text-[#D9381E]" size={40} strokeWidth={3} />
          </div>
          <h1 className="font-display text-5xl text-cream mt-6">{errorHeadline(status)}</h1>
          <p className="text-[#A19D94] mt-4 max-w-md">
            {timeoutOrError(status)}
          </p>
          <Link to="/shop" className="mt-8 inline-block px-8 h-12 border-2 border-ink-600 hover:border-brand hover:text-brand text-cream font-display text-xl tracking-widest leading-[3rem]">
            BACK TO SHOP
          </Link>
        </>
      )}
    </div>
  );
};

export default CheckoutSuccess;
